import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import theme, { mobileBreakpoint } from '../theme';
import config from '../config';
import PageLayout from './PageLayout';
import { MessageStrip, MessageIcon, MessageText } from './shared/CommonElements';
import { useAuth } from '../AuthContext';

// Keyframes for sparkles
const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
  50% { opacity: 1; transform: scale(1) rotate(180deg); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 4px 20px rgba(219, 39, 98, 0.4), 0 0 20px rgba(241, 222, 124, 0.3); }
  50% { box-shadow: 0 6px 30px rgba(219, 39, 98, 0.6), 0 0 30px rgba(241, 222, 124, 0.5); }
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 15px;
  margin-top: -5px;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto 48px auto;
  padding: 40px 24px 48px 24px;
  background: linear-gradient(135deg, #fff 100%,#fff 100%);
  border-radius: ${theme.borderRadius.large};
  box-shadow: 0 8px 32px rgba(0,0,0,0.08);
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.primary};
  margin: 0 0 8px 0;
  font-weight: 600;
`;

const SelectedSlotBox = styled.div`
  background: #f5f5f5;
  border-radius: ${theme.borderRadius.medium};
  border: 1px solid #e0e0e0;
  padding: 20px;
  text-align: center;
  margin-bottom: 24px;
`;

const SlotTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${theme.colors.secondary};
  margin-bottom: 8px;
`;

const SlotDetails = styled.div`
  font-size: 16px;
  color: #222;
  font-weight: 500;
`;

const InputLabel = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
  color: ${theme.colors.primary};
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 2px solid ${theme.colors.accent1};
  border-radius: ${theme.borderRadius.medium};
  font-size: 16px;
  font-family: inherit;
  margin-bottom: 16px;
  margin-top: 4px;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(219, 39, 98, 0.1);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${theme.colors.accent1};
  border-radius: ${theme.borderRadius.medium};
  font-size: 16px;
  font-family: inherit;
  margin-bottom: 16px;
  margin-top: 4px;
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(219, 39, 98, 0.1);
  }
`;

const ContractNotice = styled.div`
  background: #f5f5f5;
  border-radius: ${theme.borderRadius.medium};
  border: 1px solid #e0e0e0;
  padding: 18px 20px;
  color: #333;
  font-size: 15px;
  margin-top: 10px;
  text-align: center;
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 14px;
  }
`;

const PriceSection = styled.div`
  margin-bottom: 10px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${theme.colors.secondary};
`;

const AGBLink = styled.a`
  color: ${theme.colors.secondary};
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 18px 0 0 0;
  font-size: 1rem;
`;

const Checkbox = styled.input`
  margin-top: 3px;
  accent-color: ${theme.colors.secondary};
`;

const WiderrufLink = styled.a`
  color: ${theme.colors.secondary};
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  border: 1px solid #ffcdd2;
  border-radius: ${theme.borderRadius.medium};
  padding: 12px;
  margin-bottom: 20px;
  margin-top: 20px;
  font-size: 14px;
`;

const SparkleContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
  top: 0;
  left: 0;
`;

const Sparkle = styled.span`
  position: absolute;
  font-size: 10px;
  color: ${props => props.theme.colors.accent1};
  opacity: 0;
  animation: ${sparkle} 2s ease-in-out infinite;
  
  &:nth-child(1) {
    top: 20%;
    left: 15%;
    animation-delay: 0s;
  }
  
  &:nth-child(2) {
    top: 60%;
    right: 20%;
    animation-delay: 1s;
  }
  
  &:nth-child(3) {
    bottom: 25%;
    left: 25%;
    animation-delay: 0.5s;
  }
`;

const ActionButton = styled.button`
  margin: 32px auto 0 auto;
  min-width: 300px;
  display: block;
  margin-bottom: -20px;
  padding: 14px 32px;
  background: linear-gradient(135deg, ${theme.colors.secondary} 0%, #e8316d 50%, ${theme.colors.secondary} 100%);
  background-size: 200% 200%;
  color: white;
  border: 2px solid ${theme.colors.accent1};
  border-radius: ${theme.borderRadius.medium};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: visible;
  box-shadow: 0 4px 20px rgba(219, 39, 98, 0.4), 0 0 20px rgba(241, 222, 124, 0.3);
  animation: ${glow} 3s ease-in-out infinite;

  &::before {
    content: '✨';
    position: absolute;
    font-size: 14px;
    top: -8px;
    left: 10px;
    animation: ${sparkle} 2s ease-in-out infinite;
    pointer-events: none;
  }

  &::after {
    content: '⭐';
    position: absolute;
    font-size: 12px;
    bottom: -6px;
    right: 15px;
    animation: ${sparkle} 2.5s ease-in-out infinite 0.5s;
    pointer-events: none;
  }

  &:hover:enabled {
    background-position: 100% 0;
    box-shadow: 0 8px 35px rgba(219, 39, 98, 0.5), 0 0 24px rgba(241, 222, 124, 0.4);
    transform: scale(1.01);
    animation: none;
  }

  &:active:enabled {
    transform: translateY(-1px) scale(1.02);
    box-shadow: 0 4px 20px rgba(219, 39, 98, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    animation: none;
    transform: none;
    
    &:hover {
      transform: none;
      box-shadow: 0 4px 20px rgba(219, 39, 98, 0.4), 0 0 20px rgba(241, 222, 124, 0.3);
    }
  }
  @media (max-width: ${mobileBreakpoint}px) {
    min-width: unset;
    width: 100%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin: 32px 0 0 0;
  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
    align-items: center;
  }
`;

const CancelButton = styled.button`
  margin: 32px auto 0 auto;
  min-width: 300px;
  display: block;
  margin-bottom: -20px;
  padding: 14px 32px;
  background: #eee;
  color: ${theme.colors.primary};
  border: 0px solid ${theme.colors.accent1};
  border-radius: ${theme.borderRadius.medium};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    background: #f5f5f5;
    color: ${theme.colors.secondary};
    transform: scale(1.01);
  }
  @media (max-width: ${mobileBreakpoint}px) {
    min-width: unset;
    width: 100%;
  }
`;

const TrialLessonPurchasePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [billingData, setBillingData] = useState({
    name: '',
    street: '',
    zipCode: '',
    city: ''
  });
  const [email, setEmail] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [widerrufChecked, setWiderrufChecked] = useState(false);
  const [agbChecked, setAgbChecked] = useState(false);
  const [earlyExecutionChecked, setEarlyExecutionChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [billingErrors, setBillingErrors] = useState({});
  const [price, setPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(true);

  // Get slot info from navigation state
  const slotInfo = location.state;

  useEffect(() => {
    if (!slotInfo) {
      // If no slot info, redirect back
      navigate('/');
    }
  }, [slotInfo, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Fetch trial lesson price
  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const viewportWidth = window.innerWidth;
        const response = await fetch(config.getApiUrl(`/api/trial-lessons/price?viewportWidth=${viewportWidth}`), { headers });
        if (response.ok) {
          const data = await response.json();
          setPrice(data.price);
        }
      } catch (err) {
        console.error('Error fetching trial lesson price:', err);
      } finally {
        setPriceLoading(false);
      }
    };

    fetchPrice();
  }, [token]);

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    setBillingData(prev => ({ ...prev, [name]: value }));
    if (billingErrors[name]) {
      setBillingErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateBillingData = () => {
    const errors = {};
    if (!billingData.name.trim()) {
      errors.name = 'Name ist erforderlich.';
    } else if (billingData.name.length > 60) {
      errors.name = 'Name darf maximal 60 Zeichen lang sein.';
    }
    if (!billingData.street.trim()) {
      errors.street = 'Straße ist erforderlich.';
    } else if (billingData.street.length > 100) {
      errors.street = 'Straße darf maximal 100 Zeichen lang sein.';
    }
    if (!billingData.zipCode.trim()) {
      errors.zipCode = 'PLZ ist erforderlich.';
    }
    if (!billingData.city.trim()) {
      errors.city = 'Stadt ist erforderlich.';
    }
    if (!email.trim()) {
      errors.email = 'E-Mail ist erforderlich.';
    } else if (!/^\S+@\S+\.\S+$/.test(email)) {
      errors.email = 'Bitte gib eine gültige E-Mail-Adresse ein.';
    } else if (email.length > 100) {
      errors.email = 'E-Mail-Adresse darf maximal 100 Zeichen lang sein.';
    }
    if (additionalInfo && additionalInfo.length > 500) {
      errors.additionalInfo = 'Weitere Informationen dürfen maximal 500 Zeichen lang sein.';
    }
    setBillingErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    setError(null);
    
    if (!validateBillingData()) {
      return;
    }

    if (!widerrufChecked || !agbChecked || !earlyExecutionChecked) {
      setError('Bitte akzeptiere alle Bedingungen.');
      return;
    }

    setSubmitting(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const viewportWidth = window.innerWidth;
      const response = await fetch(config.getApiUrl(`/api/trial-lessons/request?viewportWidth=${viewportWidth}`), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: billingData.name,
          email: email,
          additionalInfo: additionalInfo,
          date: slotInfo.date,
          startTime: slotInfo.startTime,
          endTime: slotInfo.endTime,
          billingStreet: billingData.street,
          billingZipCode: billingData.zipCode,
          billingCity: billingData.city
        })
      });

      if (response.ok) {
        // Navigate to trial lesson success page
        navigate('/trial-lesson-success', { 
          state: { 
            slotInfo: slotInfo 
          } 
        });
      } else {
        const errorText = await response.text();
        setError(errorText || 'Fehler beim Senden der Anfrage.');
      }
    } catch (e) {
      setError('Netzwerkfehler beim Senden der Anfrage.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!slotInfo) {
    return null;
  }

  return (
    <PageLayout>
      <Container>
        <Title>Probestunde buchen</Title>
        
        <SelectedSlotBox>
          <SlotTitle>Ausgewählter Termin</SlotTitle>
          <SlotDetails>
            {slotInfo.formattedDate} um {slotInfo.startTime} - {slotInfo.endTime}
          </SlotDetails>
        </SelectedSlotBox>

        <Section>
          <SectionTitle>Rechnungsdaten</SectionTitle>
          <InputLabel>
            Vollständiger Name: *
            <Input
              type="text"
              name="name"
              value={billingData.name}
              onChange={handleBillingChange}
              placeholder="Dein vollständiger Name"
              maxLength={60}
              style={{
                borderColor: billingErrors.name ? 'red' : undefined
              }}
            />
            {billingErrors.name && (
              <div style={{ color: 'red', fontSize: '14px', marginTop: '-12px', marginBottom: '16px' }}>
                {billingErrors.name}
              </div>
            )}
          </InputLabel>

          <InputLabel>
            E-Mail-Adresse: *
            <Input
              type="email"
              name="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (billingErrors.email) {
                  setBillingErrors(prev => ({ ...prev, email: undefined }));
                }
              }}
              placeholder="deine.email@beispiel.de"
              maxLength={100}
              style={{
                borderColor: billingErrors.email ? 'red' : undefined
              }}
            />
            {billingErrors.email && (
              <div style={{ color: 'red', fontSize: '14px', marginTop: '-12px', marginBottom: '16px' }}>
                {billingErrors.email}
              </div>
            )}
          </InputLabel>

          <InputLabel>
            Straße und Hausnummer: *
            <Input
              type="text"
              name="street"
              value={billingData.street}
              onChange={handleBillingChange}
              placeholder="Musterstraße 123"
              maxLength={100}
              style={{
                borderColor: billingErrors.street ? 'red' : undefined
              }}
            />
            {billingErrors.street && (
              <div style={{ color: 'red', fontSize: '14px', marginTop: '-12px', marginBottom: '16px' }}>
                {billingErrors.street}
              </div>
            )}
          </InputLabel>

          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: '1' }}>
              <InputLabel>
                PLZ: *
                <Input
                  type="text"
                  name="zipCode"
                  value={billingData.zipCode}
                  onChange={handleBillingChange}
                  placeholder="12345"
                  style={{
                    borderColor: billingErrors.zipCode ? 'red' : undefined
                  }}
                />
                {billingErrors.zipCode && (
                  <div style={{ color: 'red', fontSize: '14px', marginTop: '-12px', marginBottom: '16px' }}>
                    {billingErrors.zipCode}
                  </div>
                )}
              </InputLabel>
            </div>
            <div style={{ flex: '2' }}>
              <InputLabel>
                Stadt: *
                <Input
                  type="text"
                  name="city"
                  value={billingData.city}
                  onChange={handleBillingChange}
                  placeholder="Musterstadt"
                  style={{
                    borderColor: billingErrors.city ? 'red' : undefined
                  }}
                />
                {billingErrors.city && (
                  <div style={{ color: 'red', fontSize: '14px', marginTop: '-12px', marginBottom: '16px' }}>
                    {billingErrors.city}
                  </div>
                )}
              </InputLabel>
            </div>
          </div>

          <InputLabel>
            Weitere Informationen (optional):
            <Textarea
              value={additionalInfo}
              onChange={(e) => {
                setAdditionalInfo(e.target.value);
                if (billingErrors.additionalInfo) {
                  setBillingErrors(prev => ({ ...prev, additionalInfo: undefined }));
                }
              }}
              placeholder="Gibt es noch etwas, das ich wissen sollte?"
              maxLength={500}
              style={{
                borderColor: billingErrors.additionalInfo ? 'red' : undefined
              }}
            />
            {billingErrors.additionalInfo && (
              <div style={{ color: 'red', fontSize: '14px', marginTop: '-12px', marginBottom: '16px' }}>
                {billingErrors.additionalInfo}
              </div>
            )}
          </InputLabel>
        </Section>

        <ContractNotice>
          <PriceSection>
            Preis: {priceLoading ? 'Lade Preis...' : (price || '[Preis für Probestunde]')}
          </PriceSection>
          Alle Preise sind Endpreise. Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.<br></br><br></br>
          Mit der Buchung erwirbst du eine Probestunde zum ausgewählten Termin. Ich werde diesen Termin innerhalb von 24 Stunden bestätigen. Sollte ich den Termin nicht innerhalb von 24 Stunden akzeptieren, wird der Kauf storniert und der Rechnungsbetrag erstattet.<br></br><br></br>
          Die Rechnung erhältst du direkt nach der der Terminbestätigung per E-Mail. Der Rechnungsbetrag ist innerhalb von 14 Tagen per Überweisung, SEPA-Lastschrift oder PayPal zu begleichen.
          <br></br><br></br>Bei Fragen erreichst du uns unter: <AGBLink href="mailto:mey@meyteaches.de">mey@meyteaches.de</AGBLink>
        </ContractNotice>

        <CheckboxContainer>
          <Checkbox
            type="checkbox"
            id="widerruf"
            checked={widerrufChecked}
            onChange={e => setWiderrufChecked(e.target.checked)}
          />
          <label htmlFor="widerruf">
            Ich habe die <WiderrufLink href="/terms#widerruf" target="_blank" rel="noopener noreferrer">Widerrufsbelehrung</WiderrufLink> gelesen und akzeptiere sie.
          </label>
        </CheckboxContainer>
        <CheckboxContainer>
          <Checkbox
            type="checkbox"
            id="agb"
            checked={agbChecked}
            onChange={e => setAgbChecked(e.target.checked)}
          />
          <label htmlFor="agb">
            Ich habe die{' '}
            <AGBLink href="/terms#agb" target="_blank" rel="noopener noreferrer">AGB</AGBLink> gelesen und akzeptiere sie.
          </label>
        </CheckboxContainer>
        <CheckboxContainer>
          <Checkbox
            type="checkbox"
            id="earlyExecution"
            checked={earlyExecutionChecked}
            onChange={e => setEarlyExecutionChecked(e.target.checked)}
          />
          <label htmlFor="earlyExecution">
            Ich stimme ausdrücklich zu, dass die Ausführung des Unterrichts vor Ablauf der Widerrufsfrist beginnen kann, wenn ich einen Termin innerhalb der 14-tägigen Widerrufsfrist buche, und mir ist bewusst, dass ich in diesem Fall für bereits erbrachte Unterrichtsstunden mein Widerrufsrecht verliere.
          </label>
        </CheckboxContainer>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <ButtonContainer>
          <CancelButton onClick={() => navigate('/')}>Abbrechen</CancelButton>
          <ActionButton 
            disabled={!widerrufChecked || !agbChecked || !earlyExecutionChecked || submitting}
            onClick={handleSubmit}
          >
            <SparkleContainer>
              <Sparkle>✨</Sparkle>
              <Sparkle>⭐</Sparkle>
              <Sparkle>✨</Sparkle>
            </SparkleContainer>
            {submitting ? 'Wird gesendet...' : 'Probestunde buchen'}
          </ActionButton>
        </ButtonContainer>
      </Container>
    </PageLayout>
  );
};

export default TrialLessonPurchasePage;

